import { gql } from '@apollo/client';

export const DATA_ENTRY_FRAGMENT = gql`
  fragment DataEntryFragment on DataEntry {
    id
    amount
    hasPotentialQualityIssues(
      input: { clientId: $clientId, startDate: $startDate, endDate: $endDate }
    )
    ledgerAccount {
      id
      name
    }
    contraLedgerAccounts {
      id
      name
    }
    journalEntry {
      id
      addOn {
        id
        name
      }
      vendorName
      dateOfTransaction
      contractor {
        id
        contractorId
        contractorName
        source1099
      }
      internalNotes {
        id
        body
        createdOn
      }
      file {
        id
        displayName
      }
      categorization {
        clientSuggestion
        selectedLedgerAccount {
          id
        }
      }
      relatedJournalEntry {
        journalEntry {
          id
        }
        type
      }
      comments {
        id
        body
      }
    }
  }
`;

const ACCOUNTING_PERIOD_BALANCE_FRAGMENT = gql`
  fragment AccountingPeriodBalanceFragment on AccountingPeriodBalance {
    id
    inPeriodBalance
    closingBalance
    openingBalance
  }
`;

export const GET_LEDGER_LIST = gql`
  ${DATA_ENTRY_FRAGMENT}
  ${ACCOUNTING_PERIOD_BALANCE_FRAGMENT}
  query getLedgerList(
    $clientId: ID!
    $startDate: RFC3339Date!
    $endDate: RFC3339Date!
    $query: String
    $ledgerAccountCategoryIds: [ID!]
    $multiSort: [DataEntriesSort!]
    $moneyInTransitSort: [DataEntriesSort!]
  ) {
    ledgerAccountsByClient(
      clientId: $clientId
      filter: { ledgerAccountCategoryIds: $ledgerAccountCategoryIds }
    ) {
      id
      active
      accountingPeriodBalance(filter: { startDate: $startDate, endDate: $endDate }) {
        ...AccountingPeriodBalanceFragment
      }
      category {
        id
        name
      }
      code
      dataEntries(
        filter: {
          startDate: $startDate
          endDate: $endDate
          pagination: { limit: 20, multiSort: $multiSort, moneyInTransitSort: $moneyInTransitSort }
          query: $query
        }
      ) {
        dataEntries {
          ...DataEntryFragment
        }
        pageInfo {
          total
        }
      }
      details
      internalNotes {
        id
        body
      }
      name
      role
      sortOrder
    }
  }
`;

export const GET_QUALITY_ISSUES = gql`
  ${DATA_ENTRY_FRAGMENT}
  query getQualityIssues($clientId: ID!, $startDate: RFC3339Date!, $endDate: RFC3339Date!) {
    qualityIssuesSummary(input: { clientId: $clientId, startDate: $startDate, endDate: $endDate }) {
      ...DataEntryFragment
    }
  }
`;

export const GET_DATA_ENTRIES_PAGE = gql`
  ${DATA_ENTRY_FRAGMENT}
  query getDataEntriesPageForLedger(
    $ledgerId: ID!
    $clientId: ID!
    $startDate: RFC3339Date!
    $endDate: RFC3339Date!
    $after: ID
    $query: String
    $multiSort: [DataEntriesSort!]
    $moneyInTransitSort: [DataEntriesSort!]
  ) {
    ledgerAccount(id: $ledgerId) {
      id
      dataEntries(
        filter: {
          startDate: $startDate
          endDate: $endDate
          pagination: {
            limit: 200
            after: $after
            multiSort: $multiSort
            moneyInTransitSort: $moneyInTransitSort
          }
          query: $query
        }
      ) {
        dataEntries {
          ...DataEntryFragment
        }
        pageInfo {
          endCursor
          hasNextPage
          total
        }
      }
    }
  }
`;

export const GET_BULK_EDIT_FORM_DATA = gql`
  query getBulkEditFormOptions($clientId: ID!) {
    addOns(clientId: $clientId) {
      id
      name
      accountType
    }
    ledgerAccounts(clientId: $clientId) {
      id
      name
    }
  }
`;

export const GET_REFRESHED_LEDGER_BALANCE = gql`
  ${ACCOUNTING_PERIOD_BALANCE_FRAGMENT}
  query getRefreshedLedgerBalance(
    $ledgerId: ID!
    $startDate: RFC3339Date!
    $endDate: RFC3339Date!
  ) {
    ledgerAccount(id: $ledgerId) {
      id
      accountingPeriodBalance(filter: { startDate: $startDate, endDate: $endDate }) {
        ...AccountingPeriodBalanceFragment
      }
    }
  }
`;

export const GET_REFRESHED_LEDGER_BALANCES = gql`
  ${ACCOUNTING_PERIOD_BALANCE_FRAGMENT}
  query getRefreshedLedgerBalances(
    $clientId: ID!
    $startDate: RFC3339Date!
    $endDate: RFC3339Date!
  ) {
    ledgerAccounts(filter: { clientId: $clientId }) {
      id
      accountingPeriodBalance(filter: { startDate: $startDate, endDate: $endDate }) {
        ...AccountingPeriodBalanceFragment
      }
    }
  }
`;
